// Searchable replacement for a facet select in the refinement bar. Registered as
// Alpine.data('facetCombobox').
//
// The page carries the busiest values of a facet, alphabetically. When that list is
// incomplete (eighteen thousand owners do not fit in a page) typing asks the server
// for the rest, counted over the same results. The chosen slug goes into a hidden
// input, so the form still submits exactly what the native select did.
export default function registerFacetCombobox(Alpine) {
    Alpine.data('facetCombobox', ({ options = [], complete = true, endpoint = '', value = '', label = '' } = {}) => ({
        options,
        complete,
        endpoint,
        value,
        term: label,
        selectedLabel: label,
        results: [],
        open: false,
        loading: false,
        highlighted: -1,
        request: null,

        init() {
            this.results = this.options;
        },

        normalize(text) {
            return String(text)
                .normalize('NFD')
                .replace(/[̀-ͯ]/g, '')
                .toLowerCase()
                .replace(/^[\s"'„“”.,-]+/, '');
        },

        async search() {
            this.open = true;
            this.highlighted = -1;

            const needle = this.normalize(this.term);
            this.results = needle === ''
                ? this.options
                : this.options
                    .filter((option) => this.normalize(option.title).includes(needle))
                    .sort((a, b) => Number(!this.normalize(a.title).startsWith(needle)) - Number(!this.normalize(b.title).startsWith(needle)));

            if (this.complete || needle.length < 2 || !this.endpoint) {
                return;
            }

            const url = new URL(this.endpoint, window.location.origin);
            new URLSearchParams(window.location.search).forEach((paramValue, key) => {
                if (key !== 'page') {
                    url.searchParams.set(key, paramValue);
                }
            });
            url.searchParams.set('term', this.term);

            this.request?.abort();
            this.request = new AbortController();
            this.loading = true;

            try {
                const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'application/json' } });
                const data = await response.json();
                this.results = data.options ?? [];
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.results = [];
                }
            } finally {
                this.loading = false;
            }
        },

        choose(option) {
            this.value = option ? option.slug : '';
            this.selectedLabel = option ? option.title : '';
            this.term = this.selectedLabel;
            this.open = false;
            this.$nextTick(() => this.$refs.input.focus());
        },

        move(step) {
            if (!this.open) {
                this.search();
            }

            const count = this.results.length + 1;
            this.highlighted = ((this.highlighted + 1 + step + count) % count) - 1;
            this.$nextTick(() => document.getElementById(this.optionId(this.highlighted))?.scrollIntoView({ block: 'nearest' }));
        },

        confirm(event) {
            if (!this.open) {
                return;
            }

            event.preventDefault();
            this.choose(this.highlighted < 0 ? null : this.results[this.highlighted]);
        },

        close() {
            this.open = false;
            this.term = this.selectedLabel;
        },

        optionId(position) {
            return `${this.$id('facet-option')}-${position < 0 ? 'all' : position}`;
        },
    }));
}
